/**
 * SearchAndFilters.tsx - Dashboard Search and Filtering Interface
 *
 * This component provides comprehensive search and filtering capabilities
 * for the fundraisers dashboard with real-time filtering and advanced options.
 *
 * Key Features:
 * - Real-time search across fundraiser fields
 * - Advanced filtering by status, amount, and date ranges
 * - Filter toggle with expandable advanced options
 * - Active filter indicators and reset functionality
 * - Responsive design for mobile and desktop
 * - Filter state persistence and management
 *
 * Search Capabilities:
 * - Name, title, and description search
 * - Organization name and category search
 * - Real-time results as user types
 * - Search term highlighting in results
 *
 * Filter Options:
 * - Status: Active, Paused, Completed, Draft, Inactive
 * - Amount Range: Minimum and maximum raised amounts
 * - Date Range: Creation date and last activity filters
 * - Category: Fundraiser category filtering
 *
 * Props:
 * - searchTerm: string - Current search term
 * - onSearchChange: (term: string) => void - Search change handler
 * - filters: FilterState - Current filter state
 * - onFiltersChange: (filters: FilterState) => void - Filter change handler
 * - showFilters: boolean - Whether advanced filters are visible
 * - onToggleFilters: () => void - Toggle filters visibility
 * - hasActiveFilters: boolean - Whether any filters are active
 * - onResetFilters: () => void - Reset all filters handler
 *
 * Dependencies: Styled-components, Lucide icons, filter state management
 */

import React from "react";
import styled from "styled-components";
import { X, Filter, RotateCcw } from "lucide-react";
import { FundRaiserStatus } from "../../enums";

const Container = styled.div`
  width: 100%;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px 14px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 16px center;
  background-size: 20px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

const FilterControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.active ? "#10b981" : "#e2e8f0")};
  background: ${(props) => (props.active ? "#f0fdf4" : "#ffffff")};
  color: ${(props) => (props.active ? "#10b981" : "#64748b")};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: #10b981;
    background: #f8fafc;
  }
`;

const FilterBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #10b981;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
`;

const ResetButton = styled.button<{ visible: boolean }>`
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: #ffffff;
  color: #64748b;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const FiltersPanel = styled.div<{ visible: boolean }>`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 24px;
  width: 100%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  overflow: hidden;
  max-height: ${(props) => (props.visible ? "300px" : "0")};
  opacity: ${(props) => (props.visible ? "1" : "0")};
  transform: ${(props) =>
    props.visible ? "translateY(0)" : "translateY(-10px)"};
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transition-property: max-height, opacity, transform, margin-bottom, padding;
  padding: ${(props) => (props.visible ? "14px 24px" : "0 24px")};
  margin-bottom: ${(props) => (props.visible ? "24px" : "0")};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 1fr) minmax(250px, 1fr) minmax(
      300px,
      1fr
    );
  gap: 20px 40px;
  align-items: start;
  justify-content: start;
  max-width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 20px 30px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 30px 20px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  width: 160px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;

  @media (max-width: 1200px) {
    width: 140px;
  }

  @media (max-width: 1000px) {
    width: 120px;
  }

  @media (max-width: 900px) {
    width: 140px;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const RangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RangeDash = styled.span`
  color: #64748b;
  font-weight: 600;
`;

const StatusCheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 5px;
`;

const CheckboxRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  min-width: 120px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

interface SearchAndFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filters: {
    status: string[];
    amountMin: string;
    amountMax: string;
    dateMin: string;
    dateMax: string;
  };
  onFiltersChange: (filters: any) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
}

export const SearchAndFilters: React.FC<SearchAndFiltersProps> = ({
  searchTerm,
  onSearchChange,
  filters,
  onFiltersChange,
  showFilters,
  onToggleFilters,
  hasActiveFilters,
  onResetFilters,
}) => {
  const handleStatusChange = (
    status: keyof typeof FundRaiserStatus,
    checked: boolean
  ) => {
    const newStatus = checked
      ? [...filters.status, status]
      : filters.status.filter((s) => s !== status);
    onFiltersChange({ ...filters, status: newStatus });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.status.length > 0) count++;
    if (filters.amountMin || filters.amountMax) count++;
    if (filters.dateMin || filters.dateMax) count++;
    return count;
  };

  const clearSearch = () => {
    onSearchChange("");
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Container>
      <FiltersPanel visible={showFilters}>
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Status</FilterLabel>
            <StatusCheckboxGroup>
              <CheckboxRow>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={filters.status.includes("Active")}
                    onChange={(e) =>
                      handleStatusChange("Active", e.target.checked)
                    }
                  />
                  Active
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={filters.status.includes("Draft")}
                    onChange={(e) =>
                      handleStatusChange("Draft", e.target.checked)
                    }
                  />
                  Draft
                </CheckboxLabel>
              </CheckboxRow>
              <CheckboxRow>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={filters.status.includes("Inactive")}
                    onChange={(e) =>
                      handleStatusChange("Inactive", e.target.checked)
                    }
                  />
                  Inactive
                </CheckboxLabel>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={filters.status.includes("Completed")}
                    onChange={(e) =>
                      handleStatusChange("Completed", e.target.checked)
                    }
                  />
                  Completed
                </CheckboxLabel>
              </CheckboxRow>
            </StatusCheckboxGroup>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Amount Range</FilterLabel>
            <RangeContainer>
              <FilterInput
                type="number"
                placeholder="Min amount"
                value={filters.amountMin}
                onChange={(e) =>
                  onFiltersChange({ ...filters, amountMin: e.target.value })
                }
              />
              <RangeDash>—</RangeDash>
              <FilterInput
                type="number"
                placeholder="Max amount"
                value={filters.amountMax}
                onChange={(e) =>
                  onFiltersChange({ ...filters, amountMax: e.target.value })
                }
              />
            </RangeContainer>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Date Range</FilterLabel>
            <RangeContainer>
              <FilterInput
                type="date"
                placeholder="Start date"
                value={filters.dateMin}
                onChange={(e) =>
                  onFiltersChange({ ...filters, dateMin: e.target.value })
                }
              />
              <RangeDash>—</RangeDash>
              <FilterInput
                type="date"
                placeholder="End date"
                value={filters.dateMax}
                onChange={(e) =>
                  onFiltersChange({ ...filters, dateMax: e.target.value })
                }
              />
            </RangeContainer>
          </FilterGroup>
        </FiltersGrid>
      </FiltersPanel>
    </Container>
  );
};
